import React, { useMemo } from 'react';

import type AnalyticsWebClient from '@atlassiansox/analytics-web-client';
import { useIntl } from 'react-intl';
import type { EntryPointProps } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import PageIcon from '@atlaskit/icon/core/page';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import { token, useThemeObserver } from '@atlaskit/tokens';
import { type EMBEDDED_CONFLUENCE_MODE, EmbeddedConfluence } from '@atlassian/embedded-confluence';
import {
	PreviewPanel,
	PreviewPanelActionGroup,
	PreviewPanelBody,
	PreviewPanelExpand,
	PreviewPanelHeader,
	PreviewPanelTitle,
} from '@atlassian/preview-panel';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';

import { type PanelDataMap, type PARENT_PRODUCTS } from '../../common/constants';

import { messages } from './messages';
import { constructUrl, initializeAnalyticsClient, onEditMode } from './utils';
export type ConfluenceContentType = 'page' | 'blogpost' | 'whiteboard';

export interface ConfluencePagePreviewPanelProps {
	cloudId?: string;
	contentType?: ConfluenceContentType;
	panelData?: PanelDataMap;
	parentProduct: (typeof PARENT_PRODUCTS)[keyof typeof PARENT_PRODUCTS];
	url: string;
}

export type ConfluencePagePreviewPanelEntrypointProps = EntryPointProps<
	Record<string, never>,
	Record<string, never>,
	ConfluencePagePreviewPanelProps,
	Record<string, never>
>;

const styles = cssMap({
	body: {
		flexGrow: 1,
		overflowY: 'hidden',
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingLeft: token('space.300'),
	},
});

const contentTypeTitleMessages = {
	page: messages.pageTitle,
	blogpost: messages.blogPostTitle,
	whiteboard: messages.whiteboardTitle,
} as const;

export const ConfluencePagePreviewPanel = ({
	url,
	parentProduct,
	cloudId,
	contentType = 'page',
	panelData,
}: ConfluencePagePreviewPanelProps): React.JSX.Element => {
	const { close, setPanelWidth } = usePreviewPanelsActions();
	const [analyticsClient, setAnalyticsClient] = React.useState<AnalyticsWebClient>();
	const { formatMessage } = useIntl();
	const themeState = useThemeObserver();
	const { actions } = panelData || {};

	React.useEffect(() => {
		setAnalyticsClient(initializeAnalyticsClient(cloudId));
	}, [cloudId, setAnalyticsClient]);

	const onModeChange = (mode: EMBEDDED_CONFLUENCE_MODE) => onEditMode(mode, setPanelWidth);

	const onClose = () => {
		actions?.onClose?.();
		close();
	};

	const formattedUrl = useMemo(
		() => constructUrl(themeState, url, panelData),
		[panelData, url, themeState],
	);
	const embeddedConfluenceSource = (() => {
		try {
			const urlObj = new URL(formattedUrl);
			return urlObj.searchParams.get('embeddedConfluenceSource');
		} catch {
			return null;
		}
	})();

	const isProjectPagesUxRevampExpEnabled = FeatureGates.getExperimentValue(
		'project-pages-ux-revamp-experiment',
		'isEnabled',
		false,
		{ fireExperimentExposure: false },
	);

	const runtimeProps = {
		url: formattedUrl,
		showFooter: false,
		hasFooterLogo: false,
		onModeChange: onModeChange,
		onPublish: actions?.onPublish,
		allowedFeatures: panelData?.allowedFeatures,
		parentProduct: parentProduct,
		embeddedConfluenceSource:
			embeddedConfluenceSource && isProjectPagesUxRevampExpEnabled
				? embeddedConfluenceSource
				: 'confluence-page-preview-panel',
		themeState: themeState,
	};

	return (
		<PreviewPanel testId="confluence-preview-panel">
			<PreviewPanelHeader
				analyticsClient={analyticsClient}
				parentProduct={parentProduct}
				panelType={'confluence-page'}
				onClose={onClose}
				onOpenInNewTab={actions?.onOpenInNewTab}
				url={formattedUrl}
			>
				<PreviewPanelTitle
					title={formatMessage(contentTypeTitleMessages[contentType])}
					icon={
						contentType === 'blogpost' ? (
							<QuotationMarkIcon label="" size="medium" />
						) : (
							<PageIcon label="" size="medium" />
						)
					}
				/>
				{!!actions && (
					<PreviewPanelActionGroup>
						{actions.onExpand && <PreviewPanelExpand onClick={actions.onExpand} />}
					</PreviewPanelActionGroup>
				)}
			</PreviewPanelHeader>
			<PreviewPanelBody
				analyticsClient={analyticsClient}
				parentProduct={parentProduct}
				panelType={'confluence-page'}
				styles={styles.body}
			>
				<EmbeddedConfluence {...runtimeProps} />
			</PreviewPanelBody>
		</PreviewPanel>
	);
};

const ConfluencePagePreviewPanelEntryPointComponent = ({
	props: { url, parentProduct, cloudId, contentType, panelData },
}: ConfluencePagePreviewPanelEntrypointProps): React.JSX.Element => (
	<ConfluencePagePreviewPanel
		url={url}
		parentProduct={parentProduct}
		cloudId={cloudId}
		contentType={contentType}
		panelData={panelData}
	/>
);

export default ConfluencePagePreviewPanelEntryPointComponent;
